import React, { useEffect, useState } from "react";
import Layout from "./../layouts/Index";
import { Row, Col, Container } from "react-bootstrap";
import { connect } from "react-redux";
import { getStatistics } from "./../../store/actions/StatisticsActions";
import BlockLoader from "./../contentloaders/BlockLoader";
import CountUp from "react-countup";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function Index(props) {
  // Props
  const {
    statistics: { data: statistics = {} },
  } = props;

  // States
  const [loading, setLoading] = useState(false);

  // Effects
  const { getStatistics } = props;
  useEffect(() => {
    async function fetchStatistics() {
      try {
        setLoading(true);
        await getStatistics();
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log(error);
      }
    }
    fetchStatistics();
  }, [getStatistics]);

  // Functions
  const buildChart = (chartData, chartDataItem, chartDataItemValueKey, loading) => {
    const chart_information = { labels: [], datasets: [] };

    if (!loading && chartData && Array.isArray(chartDataItem)) {
      const monthsInOrder = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      // Always use full year labels
      chart_information.labels = monthsInOrder;

      // Extract unique years
      const unique_years = [...new Set(chartDataItem.map((item) => item.year))];
      const datasets = [];
      if (unique_years.length > 0) {
        const colors = generateRandomColor(unique_years.length);

        unique_years.forEach((year, i) => {
          // Build a month map for quick lookup
          const monthMap = {};
          chartDataItem
            .filter((item) => item.year === year)
            .forEach((item) => {
              // get item value by key
              const value = item[chartDataItemValueKey];
              monthMap[item.month] = value;
            });

          // Ensure all months present, fill missing with 0
          const dataForYear = monthsInOrder.map(
            (month) => monthMap[month] ?? 0
          );

          datasets.push({
            label: year,
            data: dataForYear,
            borderColor: colors[i],
            backgroundColor: colors[i],
          });
        });
      }

      chart_information.datasets = datasets;
    }
    return chart_information;
  }

  const generateRandomColor = (numColors) => {
    const colors = [];
    for (let i = 0; i < numColors; i++) {
      const color = `rgba(${Math.floor(Math.random() * 256)}, ${Math.floor(
        Math.random() * 256
      )}, ${Math.floor(Math.random() * 256)}, 0.8)`;
      colors.push(color);
    }
    return colors;
  };
  const buildPieChart = (data, labelKey, valueKey, loading) => {
    if (!loading && data && Array.isArray(data)) {
      const colors = generateRandomColor(data.length);
      const isMobile = window.innerWidth <= 768; 

      const formatLabel = (label, maxLength = 20) => {
        if (!isMobile) return label;
        const words = label.split(" ");
        const lines = [];
        let currentLine = "";

        words.forEach((word) => {
          if ((currentLine + " " + word).trim().length > maxLength) {
            lines.push(currentLine.trim());
            currentLine = word;
          } else {
            currentLine += " " + word;
          }
        });

        if (currentLine) lines.push(currentLine.trim());
        return lines;
      };
      const pieChartData = {
        labels: data.map((item) => item[labelKey]),
        datasets: [
          {
            label: "Orders by Status",
            data: data.map((item) => item[valueKey]),
            backgroundColor: colors,
            borderColor: "#fff",
            borderWidth: 2,
          },
        ],
      };
      const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "right",
            labels: {
              generateLabels: (chart) => {
                const dataset = chart.data.datasets[0];
                const total = dataset.data.reduce((sum, val) => sum + val, 0);

                return chart.data.labels.map((label, i) => {
                  const value = dataset.data[i];
                  const percentage = ((value / total) * 100).toFixed(1);

                  // handle array or string labels
                  const baseLabel = Array.isArray(label) ? label.join(" ") : label;
                  const fullText = `${baseLabel} (${percentage}%)`;

                  return {
                    text: formatLabel(fullText),
                    fillStyle: dataset.backgroundColor[i],
                    hidden: isNaN(dataset.data[i]) || dataset.data[i] === null,
                    index: i,
                  };
                });
              },
            },
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const value = context.raw;
                const total = context.chart._metasets[0].total;
                const percentage = ((value / total) * 100).toFixed(2);
                return `${context.label}: ${value} (${percentage}%)`;
              },
            },
          },
        },
      };
      return { pieChartData, pieChartOptions };
    }
    return { pieChartData: {}, pieChartOptions: {} };
  };

  let users_past_year = {},
    moq_past_year = {},
    po_status_distribution = {},
    po_past_year = {},
    bids_past_year = {},
    transactions_past_year = {};

  users_past_year = buildChart(statistics, statistics?.users_past_year, 'total_users', loading);
  moq_past_year = buildChart(statistics, statistics?.moq_past_year, 'total_moq', loading);
  po_status_distribution = buildPieChart(statistics?.po_status_distribution || [], 'status', 'total_orders', loading);
  po_past_year = buildChart(statistics, statistics?.po_past_year, 'total_orders', loading);
  bids_past_year = buildChart(statistics, statistics?.bids_past_year, 'total_paid_bids', loading);
  transactions_past_year = buildChart(statistics, statistics?.transactions_past_year, 'total_amount', loading);

  let privilege = parseInt(localStorage.getItem("ltdn_priv")) || null;

  return (
    <Layout>
      <Col sm="12" style={{ minHeight: "80vh", padding: "1.5em 0" }}>
        <Container>
          <Row>
            {privilege === 2 && (
              <Col
                xs={12}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "2em",
                }}
              >
                <h4 style={{ marginBottom: "0.2em", textAlign: "center" }}>Total Customers</h4>
                {loading && statistics?.total_users === undefined ? (
                  <BlockLoader
                    width="120px"
                    height="60px"
                    innerWidth="100%"
                    innerHeight="100%"
                    style={{ marginBottom: "2em" }}
                  />
                ) : (
                  <div style={{ width: "100%", height: "60px" }}>
                    <CountUp
                      start={0}
                      end={statistics?.total_users || 0}
                      duration={8}
                      separator=","
                      decimal="."
                      prefix=""
                      suffix=""
                      style={{
                        fontSize: "2rem",
                        fontWeight: "700",
                        color: "#007bff",
                        textAlign: "center",
                        display: "block",
                      }}
                    />
                  </div>
                )}
              </Col>
            )}
            <Col
              xs={12}
              md={6}
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                flexDirection: "column",
                marginBottom: "2em",
              }}
            >
              <h4 style={{ marginBottom: "0.2em", textAlign: "center" }}>Total Products</h4>
              {loading ? (
                <BlockLoader
                  width="120px"
                  height="60px"
                  innerWidth="100%"
                  innerHeight="100%"
                  style={{ marginBottom: "2em" }}
                />
              ) : (
                <div style={{ width: "100%", height: "60px" }}>
                  <CountUp
                    start={0}
                    end={statistics?.total_products || 0}
                    duration={8}
                    separator=","
                    decimal="."
                    prefix=""
                    suffix=""
                    style={{
                      fontSize: "2rem",
                      fontWeight: "700",
                      color: "#007bff",
                      textAlign: "center",
                      display: "block",
                    }}
                  />
                </div>
              )}
            </Col>
            <Col
              xs={12}
              md={6}
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                flexDirection: "column",
                marginBottom: "2em",
              }}
            >
              <h4 style={{ marginBottom: "0.2em", textAlign: "center" }}>Total Customer Orders</h4>
              {loading ? (
                <BlockLoader
                  width="120px"
                  height="60px"
                  innerWidth="100%"
                  innerHeight="100%"
                  style={{ marginBottom: "2em" }}
                />
              ) : (
                <div style={{ width: "100%", height: "60px" }}>
                  <CountUp
                    start={0}
                    end={statistics?.total_successful_bids || 0}
                    duration={8}
                    separator=","
                    decimal="."
                    prefix=""
                    suffix=""
                    style={{
                      fontSize: "2rem",
                      fontWeight: "700",
                      color: "#007bff",
                      textAlign: "center",
                      display: "block",
                    }}
                  />
                </div>
              )}
            </Col>
            <Col
              xs={12}
              md={6}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: "1em",
              }}
            >
              <h4 style={{ marginBottom: "0.2em", textAlign: "center" }}>
                New Users Trend
              </h4>
              {!loading && Object.keys(users_past_year)?.length > 0 && users_past_year !== undefined ? (
                <div style={{ width: "100%", height: "300px" }}>
                  <Line
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "top",
                        },
                        title: {
                          display: false,
                        },
                      },
                    }}
                    data={users_past_year}
                  />
                </div>
              ) : (
                <BlockLoader
                  width="100%"
                  height="300px"
                  innerWidth="100%"
                  innerHeight="100%"
                  style={{ marginBottom: "2em" }}
                />
              )}
            </Col>
            <Col
              xs={12}
              md={6}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: "1em",
              }}
            >
              <h4 style={{ marginBottom: "0.2em", textAlign: "center" }}>
                Customer Orders Trend
              </h4>
              {!loading && Object.keys(bids_past_year)?.length > 0 && bids_past_year !== undefined ? (
                <div style={{ width: "100%", height: "300px" }}>
                  <Line
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "top",
                        },
                        title: {
                          display: false,
                        },
                      },
                    }}
                    data={bids_past_year}
                    height={300}
                  />
                </div>
              ) : (
                <BlockLoader
                  width="100%"
                  height="300px"
                  innerWidth="100%"
                  innerHeight="100%"
                  style={{ marginBottom: "2em" }}
                />
              )}
            </Col>
            <Col
              xs={12}
              md={6}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: "1em",
              }}
            >
              <h4 style={{ marginBottom: "0.2em", textAlign: "center" }}>
                Campaigns (MOQ) Trend
              </h4>
              {!loading && Object.keys(moq_past_year)?.length > 0 && moq_past_year !== undefined ? (
                <div style={{ width: "100%", height: "300px" }}>
                  <Bar
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                      legend: {
                        position: "top",
                      },
                      title: {
                        display: false,
                      },
                    },
                    }}
                    data={moq_past_year}
                    height={300}
                  />
                </div>
              ) : (
                <BlockLoader
                  width="100%"
                  height="300px"
                  innerWidth="100%"
                  innerHeight="100%"
                  style={{ marginBottom: "2em" }}
                />
              )}
            </Col>
            <Col
              xs={12}
              md={6}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: "1em",
              }}
            >
              <h4 style={{ marginBottom: "0.2em", textAlign: "center" }}>
                Purchase Order Trend
              </h4>
              {!loading && Object.keys(po_past_year)?.length > 0 && po_past_year !== undefined ? (
                <div style={{ width: "100%", height: "300px" }}>
                  <Bar
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                      legend: {
                        position: "top",
                      },
                      title: {
                        display: false,
                      },
                    },
                    }}
                    data={po_past_year}
                    height={300}
                  />
                </div>
              ) : (
                <BlockLoader
                  width="100%"
                  height="300px"
                  innerWidth="100%"
                  innerHeight="100%"
                  style={{ marginBottom: "2em" }}
                />
              )}
            </Col>
            <Col
              xs={12}
              md={6}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: "1em",
              }}
            >
              <h4 style={{ marginBottom: "0.2em", textAlign: "center" }}>
                Order Status Distribution
              </h4>
              {!loading && Object.keys(po_status_distribution.pieChartData)?.length > 0 && po_status_distribution !== undefined ? (
                <div style={{ width: "100%", height: "300px" }}>
                  <Pie data={po_status_distribution.pieChartData} options={po_status_distribution.pieChartOptions} />
                </div>
              ) : (
                <BlockLoader
                  width="100%"
                  height="300px"
                  innerWidth="100%"
                  innerHeight="100%"
                  style={{ marginBottom: "2em" }}
                />
              )}
            </Col>
            {privilege === 2 && (
              <Col
                xs={12}
                md={6}
                style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: "1em",
              }}
              >
                <h4 style={{ marginBottom: "0.2em", textAlign: "center" }}>
                  Transactions Trend
                </h4>
                {!loading && Object.keys(transactions_past_year)?.length > 0 && transactions_past_year !== undefined ? (
                  <div style={{ width: "100%", height: "300px" }}>
                    <Bar
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                        legend: {
                          position: "top",
                        },
                        title: {
                          display: false,
                        },
                      },
                      }}
                      data={transactions_past_year}
                      height={300}
                    />
                  </div>
                ) : (
                  <BlockLoader
                    width="100%"
                    height="300px"
                    innerWidth="100%"
                    innerHeight="100%"
                    style={{ marginBottom: "2em" }}
                  />
                )}
              </Col>
            )}
          </Row>
        </Container>
      </Col>
    </Layout>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    statistics: state.statistics,
  };
};

export default connect(mapStateToProps, { getStatistics })(Index);
